/* global Modalytics */
import { isFormServiceAPIPayloadLengthValid } from '../../validators';

/**
 * Rebuilds a URL string from a parsed URL without percent-encoding query values.
 *
 * `URL#toString()` re-serializes query params via `URLSearchParams`, which expands
 * characters like `"` to `%22` and inflates the payload when `landingPageURL` is
 * JSON-stringified. This joins decoded param values as-is to preserve shorter
 * literal encoding (e.g. JSON blobs appended upstream).
 *
 * @param {URL} url - A parsed URL whose search params have already been mutated.
 * @returns {string} The origin, pathname, and query string (if any).
 */
function buildLandingPageUrl(url) {
	const query = [...url.searchParams.entries()]
		.map(([name, value]) => `${name}=${value}`)
		.join('&');

	return query ?
		`${url.origin}${url.pathname}?${query}` :
		`${url.origin}${url.pathname}`;
}

/**
 * Sanitizes `formData.landingPageURL` before the API payload is sent.
 *
 * Applies a three-tier pruning strategy on `formData.landingPageURL` and always
 * returns the updated `formData`.
 * Heap events are fired at each pruning tier as observability signals.
 *
 *   1. Always removes the `cep` query parameter.
 *   2. Removes any remaining parameter whose value exceeds MAX_PARAM_LENGTH characters,
 *      firing an `Oversized Lead` Heap event for each one dropped.
 *   3. If the full payload is still too large after step 2, removes the optional
 *      geo-enrichment parameters (`HSGeolookupData`, `HSGeoLookupDetails`) and fires
 *      an `Oversized Lead` Heap event signaling optional payload reduction.
 *   4. If the payload is still too large after step 3, fires a final `Oversized Lead`
 *      Heap event and returns the best-effort pruned `formData`.
 *
 * @param {Object} formData - The formatted API payload object. Must contain a `landingPageURL` string.
 * @returns {Object} The updated formData with the sanitized `landingPageURL`.
 */
export function sanitizeLandingPageUrl(formData = {}) {
	// Maximum allowed character length for any individual query parameter value.
	const MAX_PARAM_LENGTH = 250;
	const firstStepWhitelist = ['hsgeolookupdetails', 'hsgeolookupdata'];

	// Nothing to sanitize if landingPageURL is absent.
	if (!formData.landingPageURL) {
		return formData;
	}

	let url;

	try {
		url = new URL(formData.landingPageURL);
	} catch {
		// If the URL cannot be parsed, return formData as-is and let the submission proceed.
		return formData;
	}

	// Always strip `cep` unconditionally.
	url.searchParams.delete('cep');

	// Spread into a static array snapshot before iterating so that deleting params
	// inside the loop does not affect the iterator — mutating a live URLSearchParams
	// iterator while walking it causes the internal index to skip the next entry.
	for (const [name, value] of [...url.searchParams.entries()]) {
		// If the current param is not included in the whitelist and its value exceeds the max length, drop it and fire a Heap event.
		if (!firstStepWhitelist.includes(name.toLowerCase()) && value.length > MAX_PARAM_LENGTH) {
			// Signal to analytics that a query parameter was dropped due to excess length.
			Modalytics.heap('track', ['Oversized Lead', { type: 'parameter', name, value }]);
			url.searchParams.delete(name);
		}
	}

	// Sync the cleaned URL back onto formData so the full payload size is accurate.
	formData.landingPageURL = buildLandingPageUrl(url);

	// Checkpoint 1: validate the full payload after removing oversized individual params.
	if (isFormServiceAPIPayloadLengthValid(formData)) {
		return formData;
	}

	// Payload is still too large — drop the optional geo-enrichment params.
	url.searchParams.delete('HSGeolookupData');
	url.searchParams.delete('HSGeoLookupDetails');

	// Signal to analytics that optional geo-enrichment params were dropped to reduce payload size.
	Modalytics.heap('track', ['Oversized Lead', { type: 'optionalPayload' }]);

	// Sync again after dropping optional geo params.
	formData.landingPageURL = buildLandingPageUrl(url);

	// Checkpoint 2: validate the full payload after removing optional geo params.
	// Only fire the final Heap event if the payload is still too large after all pruning.
	// the submission always proceeds, but analytics need to know when no pruning was sufficient.
	if (!isFormServiceAPIPayloadLengthValid(formData)) {
		Modalytics.heap('track', ['Oversized Lead', { type: 'payload' }]);
	}

	// Always return the updated formData regardless of checkpoint 1 or 2 outcome.
	return formData;
}
